import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// Persona -> on-screen color tuple. Kept here (rather than in the theme
// config) because every theme uses the same warm/cool split, and threading
// these through the theme objects added noise without flexibility.
const speakerStripe = (speaker) => {
  switch (speaker) {
    case 'Ada': return '248,113,113';   // red-400
    case 'Ved': return '74,222,128';    // green-400
    case 'system': return '165,180,252'; // indigo-300
    default: return '148,163,184';      // slate-400
  }
};

const formatSpeakerName = (speaker) => {
  if (speaker === 'system') return 'Radio System';
  return speaker;
};

const TranscriptPanel = ({
  showPanel,
  onClose,
  theme,
  transcriptHistory = []
}) => {
  const transcriptEndRef = useRef(null);

  useEffect(() => {
    if (transcriptEndRef.current && showPanel) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcriptHistory, showPanel]);

  // Render even when hidden so the slide-out transition can play; toggle a
  // `is-open` class to drive translate-x.
  const isLight = !!theme.isLight;

  return (
    <>
      {/* Backdrop -- dim + blur the page behind the drawer. */}
      <div
        className={`transcript-backdrop fixed inset-0 z-40 transition-opacity duration-300 ${
          showPanel ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backgroundColor: isLight ? 'rgba(15,23,42,0.20)' : 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer: full-screen sheet on mobile, right-side panel on desktop.
          Shadow only applied when open -- when closed the drawer is
          translated 100% off-screen but a constant left-falling box-shadow
          would still bleed a dark stripe back into the visible page. */}
      <aside
        className={`transcript-drawer fixed top-0 right-0 z-50 h-full w-full sm:w-[26rem] flex flex-col
          transition-transform duration-300 ease-out
          ${showPanel ? 'translate-x-0' : 'translate-x-full'}`}
        style={{
          backgroundColor: `rgba(${theme.cardBgRgb}, ${isLight ? 0.94 : 0.82})`,
          backdropFilter: 'blur(24px) saturate(140%)',
          WebkitBackdropFilter: 'blur(24px) saturate(140%)',
          borderLeft: isLight
            ? '1px solid rgba(15,23,42,0.08)'
            : '1px solid rgba(255,255,255,0.08)',
          boxShadow: showPanel
            ? (isLight
                ? '-8px 0 24px -16px rgba(15,23,42,0.18)'
                : '-8px 0 24px -16px rgba(0,0,0,0.35)')
            : 'none',
          transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out',
        }}
        aria-label="Transcript"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{
            borderColor: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.06)',
          }}
        >
          <div>
            <h3 className={`text-base font-semibold ${theme.text}`}>Transcript</h3>
            <p className={`text-xs ${theme.textTertiary} mt-0.5`}>
              {transcriptHistory.length} {transcriptHistory.length === 1 ? 'turn' : 'turns'} captured
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close transcript"
            className={`p-2 rounded-full ${theme.textSecondary} hover:${theme.text} transition-colors`}
            style={{
              backgroundColor: isLight ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.04)',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {transcriptHistory.length > 0 ? (
            <>
              {transcriptHistory.map((item, index) => {
                const stripe = speakerStripe(item.speaker);
                const nameColor =
                  item.speaker === 'Ada'
                    ? 'rgb(248,113,113)'
                    : item.speaker === 'Ved'
                      ? 'rgb(74,222,128)'
                      : item.speaker === 'system'
                        ? 'rgb(165,180,252)'
                        : undefined;
                return (
                  <div
                    key={index}
                    className="relative pl-4 pr-3 py-2.5 rounded-lg transition-colors"
                    style={{
                      backgroundColor: isLight
                        ? 'rgba(15,23,42,0.025)'
                        : 'rgba(255,255,255,0.03)',
                    }}
                  >
                    {/* Per-speaker accent stripe */}
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full"
                      style={{ backgroundColor: `rgba(${stripe}, 0.85)` }}
                    />
                    <div className="flex justify-between items-baseline gap-2 mb-1">
                      <span
                        className="text-xs font-semibold tracking-wide"
                        style={{ color: nameColor || undefined }}
                      >
                        {formatSpeakerName(item.speaker)}
                      </span>
                      <span className={`text-[10px] font-mono ${theme.textTertiary}`}>
                        {item.timestamp || ''}
                      </span>
                    </div>
                    <div className={`text-sm leading-relaxed ${theme.text}`}>
                      {item.text}
                    </div>
                  </div>
                );
              })}
              <div ref={transcriptEndRef} />
            </>
          ) : (
            <div className={`text-center py-12 ${theme.textTertiary} text-sm`}>
              Waiting for broadcast to begin&hellip;
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div
          className={`px-5 py-3 border-t text-[11px] ${theme.textTertiary} text-center`}
          style={{
            borderColor: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.06)',
          }}
        >
          Press <kbd className={`px-1 py-0.5 mx-1 rounded text-[10px] font-mono ${theme.secondary} ${theme.textSecondary}`}>L</kbd> to toggle &middot;
          <kbd className={`px-1 py-0.5 mx-1 rounded text-[10px] font-mono ${theme.secondary} ${theme.textSecondary}`}>Esc</kbd> to close
        </div>
      </aside>
    </>
  );
};

export default TranscriptPanel;
